import React, { useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { consoleLogInDev } from 'shared';
import { Divider, FieldGroup, PillButton, Row, Select, Stack, Tabs } from 'library/ui';
import { useLocation, useHistory } from 'react-router-dom';
import ShiftList from './Shifts/ShiftList';
import BusinessCalendar from './BusinessCalendar/BusinessCalendar';
import { IBusinessLocation } from 'models';
import { getBusinessLocations } from 'api/BusinessLocationApi';
import DraftList from './Drafts/DraftList';
import queryString from 'query-string';

const BIZ_TABS = ['Schedule', 'Job Listings', 'Drafts'];
const [calendar, jobs, drafts] = BIZ_TABS;

const JobCardList = () => {
    const [businessLocations, setBusinessLocations] = useState<IBusinessLocation[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<IBusinessLocation>();
    const location = useLocation();
    const history = useHistory();
    const queryParams = queryString.parse(location.search);

    const selectedTab = getSelectedTab(queryParams.initialTab as string);
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('md'));

    function getSelectedTab(tab: string | null) {
        switch (tab?.toLocaleLowerCase()) {
            case 'drafts':
                return drafts;
            case 'job listings':
                return jobs;
            default:
                return calendar;
        }
    }

    function toggleView(tab: string) {
        const params = new URLSearchParams(window.location.search);
        params.set('initialTab', tab);
        const url = `jobs?${params.toString()}`;
        history.push(url);
    }

    useEffect(() => {
        fetchBusinessLocations();
    }, []);

    function fetchBusinessLocations() {
        getBusinessLocations()
            .then((result) => {
                const paramLocation = new URLSearchParams(window.location.search).get('location');
                const location = result.results.find((l: IBusinessLocation) => l.id === parseInt(paramLocation || ''));

                setSelectedLocation(location ?? result.results[0]);
                setBusinessLocations(result.results);
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
    }

    function handleLocationChange(value: string) {
        const params = new URLSearchParams(window.location.search);
        params.set('location', value);
        const url = `jobs?${params.toString()}`;
        history.push(url);
    }

    function tabFactory(tab: string) {
        const commonProps = {
            addressId: selectedLocation?.address.id,
            businessLocationId: selectedLocation?.id,
            timezone: selectedLocation?.timezone,
        };
        switch (tab) {
            case 'Jobs':
            case 'Job Listings':
                return <ShiftList {...commonProps} />;
            case 'Drafts':
                return <DraftList />;
            default:
                return <BusinessCalendar {...commonProps} />;
        }
    }

    const locationField = (
        <FieldGroup label="Location">
            <Select
                value={selectedLocation?.id.toString() || ''}
                options={businessLocations.map((b) => ({ key: b.id.toString(), value: b.name }))}
                onChange={handleLocationChange}
                fullWidth
                tone="primary"
            />
        </FieldGroup>
    );

    const tabs = (
        <Tabs
            tabs={BIZ_TABS.map((tab) => ({ key: tab, label: tab }))}
            activeTab={selectedTab}
            onTabChange={toggleView}
        />
    );

    const qrButton =
        !isXs && selectedLocation ? (
            <PillButton
                variant="secondary"
                onClick={() => history.push(`/clock-in/?loc_id=${selectedLocation.address.id}`)}
            >
                Access QR Code
            </PillButton>
        ) : null;

    return (
        <Stack gap="md">
            {isXs ? (
                <Stack gap="md">
                    <Row justify="between" align="center" gap="md">
                        {locationField}
                        {qrButton}
                    </Row>
                    <Row justify="center">{tabs}</Row>
                </Stack>
            ) : (
                <Row justify="between" align="center" gap="md">
                    <Stack grow align="start">
                        {locationField}
                    </Stack>
                    {tabs}
                    <Stack grow align="end">
                        {qrButton}
                    </Stack>
                </Row>
            )}
            <Divider />

            {tabFactory(selectedTab)}
        </Stack>
    );
};

export default JobCardList;
